import { useAuth } from "@/services/auth.service";
import { api } from "@/utils/api";
import { formatPhoneNumber } from "@/utils/number-utils";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import { LockIcon } from "lucide-react";
import { useForm } from "react-hook-form";
import z from "zod";
import { Button } from "../generic/Button";
import { Heading } from "../generic/Heading";
import { Modal } from "../generic/Modal";
import { TextField } from "../generic/TextField";

const formSchema = z.object({
  phone: z
    .string()
    .trim()
    .regex(/[0-9]/g, "Telefonnummer skal indeholde tal")
    .regex(/^[+()0-9\- ]+$/, "Ugyldigt telefonnummer"),
});

export function TrialCTAModal(props: {
  isOpen?: boolean;
  onOpenChange?: (isOpen: boolean) => void;
  title: string;
  extraButton?: {
    label: string;
    onClick: (context: { close: () => void }) => void;
  };
}) {
  const { user } = useAuth();

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      phone: user.phoneNumber ?? "",
    },
  });

  const salesPhone = "71747812";

  const { mutateAsync: requestContact, isPending } = useMutation({
    mutationFn: (phone: string) =>
      api.post("contact/contact", {
        json: {
          page: "Lasso Sales",
          name: user.name,
          email: user.email,
          phone,
          companyName: user.organization.name,
          message: `Brugeren bad om kontakt da de blev mødt af følgende besked: "${props.title}"`,
        },
      }),
  });

  return (
    <Modal
      className="text-theme-on-background"
      size="lg"
      isOpen={props.isOpen}
      onOpenChange={props.onOpenChange}
      icon={<LockIcon className="text-theme-primary size-7" />}
    >
      {({ close }) => (
        <div className="flex w-full flex-col gap-y-4">
          <Heading className="text-center text-base" slot="title">
            {props.title}
          </Heading>

          <div className="flex items-center justify-center gap-x-3 text-sm font-medium">
            <div className="text-theme-on-background-variant">
              Ring til os på
            </div>

            <a
              className="font-medium hover:underline focus-visible:underline focus-visible:outline"
              href={`tel:${salesPhone}`}
            >
              {formatPhoneNumber(salesPhone)}
            </a>
          </div>

          <TrialCTAModalSeparator />

          <form
            className="flex flex-col gap-y-4"
            onSubmit={form.handleSubmit(async (data) => {
              await requestContact(data.phone);
              close();
            })}
          >
            <div className="text-center text-sm font-medium">
              Bliv ringet op
            </div>

            <TextField
              control={form.control}
              name="phone"
              type="tel"
              aria-label="Telefonnummer"
              placeholder="Telefonnummer"
            />

            <Button
              intent="primary"
              animate
              type="submit"
              isPending={isPending}
            >
              Send
            </Button>
          </form>

          {props.extraButton && (
            <>
              <TrialCTAModalSeparator />

              <Button
                intent="secondary"
                onClick={() => props.extraButton?.onClick({ close })}
              >
                {props.extraButton.label}
              </Button>
            </>
          )}
        </div>
      )}
    </Modal>
  );
}

export function TrialCTAModalSeparator() {
  return (
    <div className="my-4 flex items-center gap-x-4">
      <div className="border-theme-outline-variant grow border-t" />
      <div className="text-theme-on-background-variant text-sm font-medium">
        eller
      </div>
      <div className="border-theme-outline-variant grow border-t" />
    </div>
  );
}
